import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import SharedClientProvider, { SharedClientContext } from '../../../contexts/Advertiser/SharedClientContext'
import axiosService from '../../../utils/AxiosService'
import { toastError, toastSuccess } from '../../FormComponents/Toasts'
import SharePageHeader from '../SharePageHeader'
import ClientCampaignTimeline from '../../Shared/ClientTimeline/ClientCampaignTimeline'
import ClientCalendar from '../../Shared/ClientCalendar/ClientCalendar'
import ClientCalendarEventModal from '../../Shared/ClientCalendar/ClientCalendarEventModal'
import SharedClientPageHeader from './SharedClientPageHeader'
import SharedClientSummaryTab from './SharedClientSummaryTab'

// Height of SharePageHeader (fixed, matches pt-16 convention used across advertiser views)
const HEADER_H = 64
const CALENDAR_EVENTS_API = '/api/advertiser/client_share/calendar_events'

function SharedClientContent() {
  const { client, campaigns, calendarEvents, isLoading, refetchCalendarEvents } = useContext(SharedClientContext)
  const [activeTab, setActiveTab] = useState(null)
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })
  const [calendarModalOpen, setCalendarModalOpen] = useState(false)
  const [editingCalendarEvent, setEditingCalendarEvent] = useState(null)

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-gray-400">Loading…</p>
      </div>
    )
  }

  const hasCampaigns = campaigns.length > 0
  const effectiveTab = activeTab ?? (hasCampaigns ? 'summary' : 'timeline')
  const activeCampaigns = campaigns.filter((c) => !c.is_finished)
  const endedCampaigns = campaigns.filter((c) => c.is_finished)

  const openNewCalendarEntry = () => { setEditingCalendarEvent(null); setCalendarModalOpen(true) }
  const openEditCalendarEntry = (event) => { setEditingCalendarEvent(event); setCalendarModalOpen(true) }
  const closeCalendarModal = () => setCalendarModalOpen(false)

  const handleSaveCalendarEvent = async (form, id) => {
    const payload = { client_calendar_event: form }
    try {
      if (id) {
        await axiosService.patch(`${CALENDAR_EVENTS_API}/${id}`, payload)
        toastSuccess('Event updated')
      } else {
        await axiosService.post(CALENDAR_EVENTS_API, payload)
        toastSuccess('Event created')
      }
      refetchCalendarEvents()
    } catch (err) {
      throw new Error(err?.data?.error || (id ? 'Failed to update event' : 'Failed to create event'))
    }
  }

  const handleDeleteCalendarEvent = async (id) => {
    try {
      await axiosService.delete(`${CALENDAR_EVENTS_API}/${id}`)
      toastSuccess('Event deleted')
      refetchCalendarEvents()
    } catch (err) {
      toastError(err?.data?.error || 'Failed to delete event')
      throw err
    }
  }

  return (
    <div
      className="flex flex-col bg-rb-grey dark:bg-rb-dark-mode-background"
      style={{ height: '100vh', paddingTop: HEADER_H }}
    >
      <SharedClientPageHeader
        clientName={client.name}
        activeTab={effectiveTab}
        onTabChange={setActiveTab}
        onNewCalendarEntry={openNewCalendarEntry}
        showSummaryTab={hasCampaigns}
      />

      <div className="min-h-0 flex-1 overflow-hidden">
        {effectiveTab === 'summary' && hasCampaigns && (
          <SharedClientSummaryTab campaigns={campaigns} />
        )}
        {effectiveTab === 'timeline' && (
          <ClientCampaignTimeline
            campaigns={activeCampaigns}
            endedCampaigns={endedCampaigns}
            hideCampaignLink
          />
        )}
        {effectiveTab === 'calendar' && (
          <ClientCalendar
            events={calendarEvents}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            onEdit={openEditCalendarEntry}
          />
        )}
      </div>

      <ClientCalendarEventModal
        isOpen={calendarModalOpen}
        onClose={closeCalendarModal}
        onSave={handleSaveCalendarEvent}
        onDelete={handleDeleteCalendarEvent}
        initialData={editingCalendarEvent}
      />
    </div>
  )
}

export default observer(function AdvertiserSharedClient() {
  return (
    <SharedClientProvider>
      <SharePageHeader />
      <SharedClientContent />
    </SharedClientProvider>
  )
})
